import { z } from 'zod'

/**
 * Correlacoes entre Itens de Trabalho (spec 13.7).
 *
 * Hierarquia != correlacao. Pai-filho e decomposicao do trabalho; relacao e
 * dependencia, bloqueio, duplicidade, implementacao ou criterio de aceite.
 *
 * Toda relacao e armazenada uma unica vez, com origem e destino, e apresentada
 * dos dois lados usando label direto e reverso.
 */

export const workItemRelationSchema = z.object({
  id: z.string().uuid(),
  sourceWorkItemId: z.string().uuid(),
  targetWorkItemId: z.string().uuid(),
  relationTypeId: z.string().uuid(),
  /** So relevante para criterio de aceite: obrigatorio bloqueia a conclusao. */
  isRequired: z.boolean(),
})
export type WorkItemRelation = z.infer<typeof workItemRelationSchema>

/** Como a relacao aparece a partir de um dos lados. */
export type RelationDirection = 'forward' | 'reverse'

export type RelationView = {
  relationId: string
  relationTypeId: string
  direction: RelationDirection
  /** Chave i18n do label a exibir deste lado da relacao. */
  labelKey: string
  otherWorkItemId: string
  isRequired: boolean
}

/**
 * Projeta uma relacao a partir da perspectiva de `viewpointWorkItemId`.
 *
 * Se o item e a origem, ve o label direto ("Bloqueia"); se e o destino, ve o
 * reverso ("E bloqueado por"). Tipos simetricos usam o mesmo label dos dois lados.
 */
export function viewRelation(
  relation: WorkItemRelation,
  relationType: { id: string; code: string; isSymmetric: boolean },
  viewpointWorkItemId: string,
): RelationView | null {
  const isSource = relation.sourceWorkItemId === viewpointWorkItemId
  const isTarget = relation.targetWorkItemId === viewpointWorkItemId
  if (!isSource && !isTarget) return null

  const direction: RelationDirection = isSource ? 'forward' : 'reverse'
  const suffix = relationType.isSymmetric || direction === 'forward' ? 'forward' : 'reverse'
  return {
    relationId: relation.id,
    relationTypeId: relation.relationTypeId,
    direction,
    labelKey: `relation.${relationType.code.toLowerCase()}.${suffix}`,
    otherWorkItemId: isSource ? relation.targetWorkItemId : relation.sourceWorkItemId,
    isRequired: relation.isRequired,
  }
}

export type RelationError =
  | { kind: 'self-relation' }
  | { kind: 'duplicate'; existingRelationId: string }

/**
 * Impede relacao do item com ele mesmo e duplicidade do mesmo par + tipo
 * (spec 13.7). A duplicidade e checada nos dois sentidos para tipos simetricos.
 */
export function validateRelation(
  candidate: Pick<WorkItemRelation, 'sourceWorkItemId' | 'targetWorkItemId' | 'relationTypeId'>,
  existing: readonly WorkItemRelation[],
  relationType: { isSymmetric: boolean },
): RelationError | null {
  if (candidate.sourceWorkItemId === candidate.targetWorkItemId) return { kind: 'self-relation' }

  const duplicate = existing.find((r) => {
    if (r.relationTypeId !== candidate.relationTypeId) return false
    const sameOrder =
      r.sourceWorkItemId === candidate.sourceWorkItemId && r.targetWorkItemId === candidate.targetWorkItemId
    if (sameOrder) return true
    if (!relationType.isSymmetric) return false
    return r.sourceWorkItemId === candidate.targetWorkItemId && r.targetWorkItemId === candidate.sourceWorkItemId
  })
  return duplicate ? { kind: 'duplicate', existingRelationId: duplicate.id } : null
}
